import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Modal, ModalBody } from "reactstrap";
import SearchCustomerField from "./SearchCustomerField";

import InputTextComponent from "../../../components/forms/InputTextComponent";
import DeliveryDistanceAndChargesBox from "./DeliveryDistanceAndChargesBox";
import DeliveryAddressBox from "./DeliveryAddressBox";
import MoreDetailsBox from "./MoreDetailsBox";
import { checkFieldError } from "./CustomerFormUtil";

const guidUtil = require("../../../utils/guid/guidUtil");
const constants = require("../../../utils/domain/constants.js");
const jobsServer = require("../../../utils/jobs/jobsServer");
const rmsApiProxyRetry = require("../../../utils/api/rmsApiProxyRetry");
const commonUtility = require("../../../utils/domain/commonUtility");
const validator = require("../../../utils/validator/validator");
const translator = require("../../../utils/translator/translator");
const stringUtil = require("../../../utils/string/stringUtil");
const storefrontUtility = require("../../../utils/domain/storefrontUtility");
const stateManager = require("../../../utils/state/stateManager");
const navigationDialogueAction = require("../../../utils/state/actions/views/navigationDialogue/navigationDialogueAction");

const initialState = {
     id: null,
     name: "",
     phoneNumber: "",
     address: "",
     address2: "",
     address3: "",
     address4: "",
     dateOfBirth: "",
     emailAddress: "",

     isDisabled: false,
     usingIgnisMobileApp: false,
     mobileMessagingToken: "",
     creditCardIntegration: "",
           
     source: constants.channels.POS,
};

const CustomerForm = ({
     dispatchType = constants.DispatchTypeDelivery,
     existingCustomer = {},
     selectedAddress = "",
     deliveryDistance:_deliveryDistance,
     deliveryCharges:_deliveryCharges,
     onSubmit = (customer, deliveryData) => {},
     onCancel = () => {},
}) => {
     const [customer, setCustomer] = useState({ ...initialState, ...existingCustomer });
     const [customerSelected, setCustomerSelected] = useState(false);
     const isDeliveryDistanceProvided = useRef(_deliveryDistance !== null && _deliveryDistance !== undefined);

     const getSelectedAddressKey = () => {
          const { address = "", address2 = "", address3 = "", address4 = "" } = existingCustomer;

          if (selectedAddress === "") return "address";

          if (address === selectedAddress) return "address";
          if (address2 === selectedAddress) return "address2";
          if (address3 === selectedAddress) return "address3";
          if (address4 === selectedAddress) return "address4";

          return "address";
     };

     const [selectedAddressKey, setSelectedAddressKey] = useState(getSelectedAddressKey());
     const [deliveryDistance, setDeliveryDistance] = useState(_deliveryDistance ?? "");
     const [deliveryCharges, setDeliveryCharges] = useState(_deliveryCharges ?? "");
     const [blockedAddressReason, setBlockedAddressReason] = useState(null);

     const [showError, setShowError] = useState(false);

     const [isDataUpdated, setIsDataUpdated] = useState(false);
     const checkIsDataUpdatedReverseCount = useRef(-1);

     const saveCustomer = useCallback(
          (customerData) => {
               customerData.phoneNumber = validator.formatPhoneNumber(customerData.phoneNumber, commonUtility.getFranchisor().country);

               if (customerData.id === null) {
                    customerData.id = guidUtil.generateGuid();
                    jobsServer.addJob(guidUtil.generateGuid(), "Adding New Customer", async () => {
                         await rmsApiProxyRetry.post(`${rmsApiProxyRetry.getFranchisorOrgContextUrl()}/customers`, customerData);
                         return true;
                    });
               } else if (isDataUpdated) {
                    jobsServer.addJob(guidUtil.generateGuid(), "Updating Existing Customer", async () => {
                         await rmsApiProxyRetry.post(`${rmsApiProxyRetry.getFranchisorOrgContextUrl()}/customers`, customerData);
                         return true;
                    });
               }
          },
          [isDataUpdated]
     );

     const closeDialog = () => {
          stateManager.getStore().dispatch(navigationDialogueAction.createAction(null));
     };

     const handleOnCancel = () => {
          closeDialog();
          onCancel();
     };

     const isAnyError = () => {
          if (!stringUtil.isStringNullOrEmpty(blockedAddressReason)) return true;
          if (checkFieldError("name", customer.name) !== null) return true;
          if (checkFieldError("phoneNumber", customer.phoneNumber, { canOrderWithoutCustomerPhoneNumber }) !== null) return true;

          if (dispatchType === constants.DispatchTypeDelivery) {
               if (checkFieldError("deliveryDistance", deliveryDistance) !== null) return true;
               if (checkFieldError("deliveryCharges", deliveryCharges) !== null) return true;
               if (
                    checkFieldError("address", selectedAddressKey, {
                         address: customer.address,
                         address2: customer.address2,
                         address3: customer.address3,
                         address4: customer.address4,
                    }) !== null
               )
                    return true;
          }

          return false;
     };

     const handleOnDone = () => {
          if (isAnyError()) {
               setShowError(true);
               return;
          }

          if (dispatchType === constants.DispatchTypeDelivery) {
               let temp = customer["address"];
               customer["address"] = customer[selectedAddressKey];
               customer[selectedAddressKey] = temp;
               setSelectedAddressKey("address");

               saveCustomer(customer);
          } else {
               saveCustomer(customer);
          }

          onSubmit(customer, { deliveryCharges, deliveryDistance, selectedAddress: customer["address"] });
          closeDialog();
     };

     const handleOnSearch = () => {
          setDeliveryCharges("");
          setDeliveryDistance("");
          setBlockedAddressReason(null);
          setSelectedAddressKey("address");
          setShowError(false);

          setCustomer({
               ...initialState,
          });
          setCustomerSelected(false);
     };
     const handleOnSkip = () => {
          setCustomerSelected(true);
     };

     const onCustomerSelect = useCallback((customerValues) => {
          setDeliveryCharges("");
          setDeliveryDistance("");
          setBlockedAddressReason(null);
          setSelectedAddressKey("address");

          setCustomer({
               ...initialState,
               ...customerValues,
          });
          setCustomerSelected(true);
     }, []);

     const handleInputChange = useCallback((value, name) => {
          setCustomer((prev) => ({ ...prev, [name]: value }));
     }, []);

     const handleOnAddressChange = useCallback((addressKey, value) => {
          setCustomer((prev) => ({
               ...prev,
               [addressKey]: value,
          }));
     }, []);

     const canOrderWithoutCustomerPhoneNumber = useCallback(() => {
          const storefrontOperatingConfiguration = storefrontUtility.getCurrentStorefront().operatingConfiguration || {};
          return storefrontOperatingConfiguration.canOrderWithoutCustomerPhoneNumber || false;
     }, []);

     useEffect(() => {
          if (checkIsDataUpdatedReverseCount.current === 0) {
               setIsDataUpdated(true);
          } else {
               checkIsDataUpdatedReverseCount.current = checkIsDataUpdatedReverseCount.current + 1;
          }
     }, [customer]);

     useEffect(() => {
          if (Object.values(existingCustomer).length > 0) {
               setCustomerSelected(true);
          }
     }, [existingCustomer]);

     return (
          <Modal
               isOpen={true}
               size="lg"
               animation={false}
               fade={false}
               style={{ maxWidth: "80vw", height: "100%", maxHeight: "calc(50vh - 1.75rem)" }}
          >
               <div className="d-flex flex-row align-items-center p-3 border-bottom">
                    <div className="window-title">{translator.getLabel("customer")}</div>
                    <div className="flex-grow-1 d-flex justify-content-end gap-2">
                         {customerSelected && (
                              <Button className="btn-action" onClick={handleOnSearch}>
                                   {translator.getLabel("search")}
                              </Button>
                         )}
                         <Button className="btn-cancel" onClick={handleOnCancel}>
                              {translator.getLabel("cancel")}
                         </Button>
                         {customerSelected ? (
                              <Button className="btn-ok" onClick={handleOnDone}>
                                   {translator.getLabel("done")}
                              </Button>
                         ) : (
                              <Button className="btn-ok" onClick={handleOnSkip}>
                                   {translator.getLabel("skip")}
                              </Button>
                         )}
                    </div>
               </div>
               <ModalBody>
                    {!customerSelected ? (
                         <SearchCustomerField onSelect={onCustomerSelect} />
                    ) : (
                         <div className="py-1">
                              <div className="d-flex flex-row gap-3 align-items-start">
                                   <Col className="d-flex flex-column">
                                        <div className="d-flex flex-column gap-3">
                                             <CustomerNameField value={customer.name} required onChange={handleInputChange} showError={showError} />
                                             <CustomerPhoneNoField
                                                  value={customer.phoneNumber}
                                                  required={!canOrderWithoutCustomerPhoneNumber()}
                                                  onChange={handleInputChange}
                                                  showError={showError}
                                                  canOrderWithoutCustomerPhoneNumber={canOrderWithoutCustomerPhoneNumber()}
                                             />
                                        </div>

                                        <MoreDetailsBox
                                             dateOfBirth={customer.dateOfBirth}
                                             emailAddress={customer.emailAddress}
                                             onChange={handleInputChange}
                                             showError={showError}
                                        />
                                   </Col>
                                   {dispatchType === constants.DispatchTypeDelivery && (
                                        <Col className="d-flex flex-column gap-3">
                                             <DeliveryAddressBox
                                                  selectedAddressKey={selectedAddressKey}
                                                  setSelectedAddressKey={setSelectedAddressKey}
                                                  address={customer.address}
                                                  address2={customer.address2}
                                                  address3={customer.address3}
                                                  address4={customer.address4}
                                                  onChange={handleOnAddressChange}
                                                  showError={showError}
                                             />

                                             <DeliveryDistanceAndChargesBox
                                                  selectedAddress={customer[selectedAddressKey]}
                                                  deliveryDistance={deliveryDistance}
                                                  deliveryCharges={deliveryCharges}
                                                  blockedAddressReason={blockedAddressReason}
                                                  setBlockedAddressReason={setBlockedAddressReason}
                                                  setDeliveryDistance={setDeliveryDistance}
                                                  setDeliveryCharges={setDeliveryCharges}
                                                  showError={showError}
                                                  isDeliveryDistanceProvided={isDeliveryDistanceProvided}
                                             />
                                        </Col>
                                   )}
                              </div>
                         </div>
                    )}
               </ModalBody>
          </Modal>
     );
};

export default CustomerForm;

const CustomerNameField = memo(({ value, required = false, onChange, showError = false }) => {
     const [error, setError] = useState(null);

     useEffect(() => {
          if (showError) setError(checkFieldError("name", value));
     }, [showError, value]);

     return (
          <InputTextComponent
               autoComplete="off"
               name="name"
               required={required}
               type="text"
               placeholder={translator.getLabel("name")}
               value={value}
               onValueChange={onChange}
               error={error}
               inputTextType="text"
          />
     );
});

const CustomerPhoneNoField = memo(({ value, required = false, onChange, showError = false, canOrderWithoutCustomerPhoneNumber }) => {
     const [error, setError] = useState(null);

     useEffect(() => {
          if (showError) setError(checkFieldError("phoneNumber", value, { canOrderWithoutCustomerPhoneNumber }));
     }, [showError, value, canOrderWithoutCustomerPhoneNumber]);

     return (
          <InputTextComponent
               autoComplete="off"
               name="phoneNumber"
               required={required}
               type="tel"
               placeholder={translator.getLabel("phone_number")}
               value={value}
               onValueChange={onChange}
               error={error}
          />
     );
});
